// ============================================================================
// LES PORTÉES D'UN MOTIF — à quoi sert chaque ligne du référentiel `justification`.
//
// UTILITÉ : un seul référentiel porte les motifs de tous les gestes qui en demandent un (écart de caisse, remise,
// crédit de cagnotte) ; sa portée dit où chacun est proposé. La Console la pose au semis, le back-office l'édite, la
// caisse la lit pour proposer ses motifs — d'où ce module au contrat.
//
// ⚠️ UNE LISTE FERMÉE DE CODES : une portée que personne ne lit créerait des motifs invisibles, et deux vocabulaires
//    de la même portée se manquent sans erreur — la Console semait « Écart de caisse » là où le back-office cherchait
//    `cash_variance`, et un magasin neuf n'avait aucun motif pour justifier un écart.
// ⚠️ UN MOTIF SANS PORTÉE, ou sous un domaine historique écrit en clair, n'est proposé à aucun de ces gestes (il reste
//    lisible là où il a servi).
// ============================================================================
import { normalizeCode } from './master-data-ids';

/** Les portées qu'un geste lit. */
export const JUSTIFICATION_SCOPES = ['cash_variance', 'discount', 'loyalty_credit'] as const;

export type JustificationScope = typeof JUSTIFICATION_SCOPES[number];

/**
 * LE MOTIF PROPOSÉ D'OFFICE, par son CODE (décision utilisateur : « Geste commercial ») — l'opérateur n'a rien à
 * choisir pour un cas ordinaire, et le motif reste enregistré.
 * ⚠️ PAR SON CODE, PAS PAR SON RANG : un magasin qui réordonne sa liste ne change pas le défaut. Ce motif ne se
 *    supprime pas (il se renomme) ; un magasin qui ne l'a pas encore reçu retombe sur le premier de la liste.
 * ⚠️ DEUX CODES : l'identité d'un motif est son code, unique dans tout le référentiel, et un motif n'a qu'une portée.
 */
export const DEFAULT_REASON_CODE: Readonly<Record<'discount' | 'loyalty_credit', string>> = {
  discount: 'geste_commercial',
  loyalty_credit: 'credit_geste_commercial',
};

/**
 * La portée d'un motif proposé d'office — `null` pour tout autre motif. Un défaut ne se supprime pas et ne change pas de
 * portée : le geste qui le propose ne le trouverait plus.
 */
export function defaultReasonScope(code: string): 'discount' | 'loyalty_credit' | null {
  for (const scope of ['discount', 'loyalty_credit'] as const) if (DEFAULT_REASON_CODE[scope] === code) return scope;
  return null;
}

/** Les portées qui présélectionnent un motif. ⚠️ Un écart se justifie toujours par un choix explicite : aucun défaut. */
export const DEFAULTABLE_SCOPES = ['discount', 'loyalty_credit'] as const;

export type DefaultableScope = typeof DEFAULTABLE_SCOPES[number];

/** Cette portée présélectionne-t-elle un motif ? */
export function isDefaultableScope(scope: string | null | undefined): scope is DefaultableScope {
  return scope === 'discount' || scope === 'loyalty_credit';
}

/** Ce que la règle du défaut lit d'un motif. */
export interface DefaultReasonCandidate {
  code: string;
  scope: string | null;
  /** Proposé (non masqué). */
  isActive: boolean;
  /** Marqué par le magasin comme son motif par défaut. */
  isDefault: boolean;
}

/**
 * LE MOTIF PRÉSÉLECTIONNÉ D'UNE PORTÉE — celui que le magasin a marqué, sinon le défaut du contrat (« Geste commercial »),
 * sinon le premier proposé ; `null` pour une portée sans défaut ou sans motif proposé.
 * ⚠️ UNE RÈGLE POUR LA CAISSE ET LE SIÈGE : la caisse présélectionne, le siège montre le bouton radio et interdit de masquer
 *    ou de supprimer ce motif. Deux lectures divergeraient, et le siège afficherait un défaut que la caisse ne propose pas.
 * ⚠️ UN MOTIF MASQUÉ N'EST JAMAIS LE DÉFAUT, même marqué : il n'est plus proposé.
 * ⚠️ `rows` dans l'ordre de la portée (`sort_order`) : c'est lui qui désigne « le premier ».
 */
export function effectiveDefaultReason<T extends DefaultReasonCandidate>(rows: readonly T[], scope: string | null): T | null {
  if (!isDefaultableScope(scope)) return null;
  const offered = rows.filter((r) => r.scope === scope && r.isActive);
  return offered.find((r) => r.isDefault) ?? offered.find((r) => r.code === DEFAULT_REASON_CODE[scope]) ?? offered[0] ?? null;
}

/** La longueur d'un code de motif proposé (dessin de l'écran des motifs). */
const PROPOSED_CODE_MAX = 24;

/**
 * LE CODE PROPOSÉ POUR UN MOTIF NOMMÉ PAR SON LIBELLÉ — « Erreur de prix » → ERREUR-DE-PRIX, unique parmi `taken`
 * (suffixe -2, -3…), 24 caractères au plus.
 * ⚠️ UNE RÈGLE POUR L'ÉCRAN DES MOTIFS ET LA REPRISE DU LEGACY : un motif repris reçoit le code que le magasin lui aurait
 *    donné en le créant lui-même, et deux règles finiraient par donner deux codes au même libellé.
 */
export function proposeReasonCode(label: string, taken: readonly string[]): string {
  const words = label.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[^A-Z0-9]+/g, '-');
  const base = cutCode(words, PROPOSED_CODE_MAX) || 'MOTIF';
  const used = new Set(taken.map(normalizeCode));
  let code = base;
  for (let n = 2; used.has(normalizeCode(code)); n++) code = `${cutCode(base, PROPOSED_CODE_MAX - 3)}-${n}`;
  return code;
}

/** Un code coupé à `max` caractères, sans tiret au bord — une coupe sur un tiret donnerait « PAR--2 ». */
function cutCode(code: string, max: number): string {
  return code.slice(0, max).replace(/^-+|-+$/g, '');
}

/** Une portée connue ? — garde des écritures (on ne range jamais un motif sous une portée que personne ne lit). */
export function isJustificationScope(value: unknown): value is JustificationScope {
  return typeof value === 'string' && (JUSTIFICATION_SCOPES as readonly string[]).includes(value);
}

/** Minuscules, sans accent, espaces réduits — pour reconnaître un domaine écrit en clair. */
function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/** Les domaines en clair que la Console porte, et leur portée. */
const DOMAIN_SCOPE: Readonly<Record<string, JustificationScope>> = {
  'ecart de caisse': 'cash_variance',
  'ecarts de caisse': 'cash_variance',
  remise: 'discount',
  remises: 'discount',
  'credit de cagnotte': 'loyalty_credit',
};

/**
 * LA PORTÉE D'UN MOTIF DÉCRIT PAR SON DOMAINE — celui que l'éditeur écrit en clair dans la Console (« Écart de caisse »,
 * « Remise »), ou déjà un code. `null` : aucun geste ne le propose.
 */
export function justificationScopeOf(domain: string | null | undefined): JustificationScope | null {
  if (!domain) return null;
  if (isJustificationScope(domain)) return domain;
  return DOMAIN_SCOPE[fold(domain)] ?? null;
}
